import { LoginRequest, RestoreRequest } from "@Dao/requestTypes";
import { LoginResponse } from "@Dao/responseTypes";
import { LoginSuccess } from "@State/actions/actionCreators";
import { Dispatch } from "redux";
import { changeAppLanguage } from "@Localization/utils";
import { Role } from "@Constants/types";
import getAPI from "@API/index";
import axios from "axios";
import { REACT_APP_BACKEND_URL } from "@Constants/tokens";

type AuthCredentials = {
  token: string;
  refreshToken?: string;
  userId: string;
  userName: string;
  role: string;
  customerId?: string;
  clientId?: string;
  defaultLanguage?: string;
  headerLogoImg?: string;
  ["18nLngCustom"]?: string;
};

export function saveAuthCredentials(config: AuthCredentials) {
  let prop: keyof typeof config;
  for (prop in config) {
    localStorage.setItem(prop, config[prop] || "");
  }
}

export function clearAuthSession() {
  localStorage.removeItem("token");
  localStorage.removeItem("refreshToken");
  localStorage.removeItem("userId");
  localStorage.removeItem("userName");
  localStorage.removeItem("role");
  localStorage.removeItem("clientId");
  localStorage.removeItem("customerId");
}

export async function loginByAccessToken(accessToken: string): Promise<boolean> {
  clearAuthSession();
  const data = { accessToken };
  try {
    const response: LoginResponse = await getAPI().request<LoginResponse>({
      method: "post",
      url: "/api/Auth/LoginByToken",
      data,
      errorText: "snackbar.incorrectToken",
      suppressSnackbar: true,
    });
    processAuth(response);
    return true;
  } catch (e) {
    return false;
  }
}

function processAuth(response: LoginResponse) {
  const authConfig: AuthCredentials = {
    token: response.accessToken,
    refreshToken: response.refreshToken || undefined,
    userId: response.userId,
    userName: response.userName,
    role: response.role,
    headerLogoImg: response.headerLogoImg,
  };
  if (response.customerId !== null) {
    authConfig.customerId = response.customerId;
  }
  if (response.clientId !== null) {
    authConfig.clientId = response.clientId;
  }

  changeAppLanguage(response.defaultLanguage);
  saveAuthCredentials(authConfig);
}

/**
 * Attempts to refresh the access token using the refresh token.
 * Returns the new access token on success, or null on failure.
 * Uses axios directly to avoid circular dependency with getAPI().
 */
export async function refreshAccessToken(): Promise<string | null> {
  const currentToken = localStorage.getItem("token");
  const refreshToken = localStorage.getItem("refreshToken");

  if (!currentToken || !refreshToken) {
    return null;
  }

  try {
    const response = await axios.post<LoginResponse>(
      `${REACT_APP_BACKEND_URL()}/api/Auth/RefreshToken`,
      {
        accessToken: currentToken,
        refreshToken: refreshToken,
      }
    );

    if (response.data?.accessToken) {
      processAuth(response.data);
      return response.data.accessToken;
    }
    return null;
  } catch (e) {
    console.warn("Token refresh failed:", e);
    return null;
  }
}

export function login(data: LoginRequest) {
  return async (dispatch: Dispatch): Promise<void> => {
    const response: LoginResponse = await getAPI().request<LoginResponse>({
      method: "post",
      url: "/api/Auth/Login",
      data,
      errorText: "snackbar.incorrectCredentials",
    });
    processAuth(response);
    dispatch(LoginSuccess());
  };
}

export async function restore(data: RestoreRequest) {
  await getAPI().request<LoginResponse>({
    method: "post",
    url: "/api/Account/forgot-password",
    data,
    successText: "snackbar.passwordResetLinkSent",
    errorText: "snackbar.incorrectEmail",
  });
}
